import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Activity, Clock, DollarSign, Server } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TraceHierarchyView } from '../components/TraceHierarchyView';
import api from '../lib/api';

const TraceDetail = () => {
  const { traceId } = useParams();
  const navigate = useNavigate();
  const [spans, setSpans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTraceSpans = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/traces/${traceId}`);
        setSpans(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching trace:', err);
        setError(err.response?.data?.detail || 'Failed to load trace details');
      } finally {
        setLoading(false);
      }
    };

    if (traceId) {
      fetchTraceSpans();
    }
  }, [traceId]);

  // Calculate aggregate metrics
  const aggregateMetrics = React.useMemo(() => {
    if (!spans || spans.length === 0) return null;

    const totalCost = spans.reduce((sum, s) => sum + (s.cost_est_usd || 0), 0);
    const totalDuration = Math.max(...spans.map(s => s.duration_ms || 0));
    const totalInputTokens = spans.reduce((sum, s) => sum + (s.input_tokens || 0), 0);
    const totalOutputTokens = spans.reduce((sum, s) => sum + (s.output_tokens || 0), 0);
    const providers = [...new Set(spans.map(s => s.provider))].join(', ');
    const status = spans.every(s => s.status === 'success') ? 'success' : 'error';

    return {
      totalCost,
      totalDuration,
      totalInputTokens,
      totalOutputTokens,
      totalSpans: spans.length,
      providers,
      status,
    };
  }, [spans]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Activity className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading trace details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header with back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-blue-600 hover:text-blue-800 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Distributed Trace Detail</h1>
        <p className="text-gray-600 mt-2 font-mono text-sm">Trace ID: {traceId}</p>
      </div>

      {/* Aggregate Metrics Cards */}
      {aggregateMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Spans</p>
                  <p className="text-2xl font-bold">{aggregateMetrics.totalSpans}</p>
                </div>
                <Activity className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Duration</p>
                  <p className="text-2xl font-bold">{aggregateMetrics.totalDuration}ms</p>
                </div>
                <Clock className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Cost</p>
                  <p className="text-2xl font-bold">${aggregateMetrics.totalCost.toFixed(4)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Providers</p>
                  <p className="text-lg font-semibold truncate">{aggregateMetrics.providers}</p>
                </div>
                <Server className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Token Stats */}
      {aggregateMetrics && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Token Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-600">Input Tokens</p>
                <p className="text-xl font-bold text-blue-600">
                  {aggregateMetrics.totalInputTokens.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Output Tokens</p>
                <p className="text-xl font-bold text-green-600">
                  {aggregateMetrics.totalOutputTokens.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Tokens</p>
                <p className="text-xl font-bold text-purple-600">
                  {(aggregateMetrics.totalInputTokens + aggregateMetrics.totalOutputTokens).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trace Hierarchy View */}
      <TraceHierarchyView spans={spans} />

      {/* Span Details Table */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>All Spans (Flat View)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Operation</th>
                  <th className="text-left py-2 px-3">Provider</th>
                  <th className="text-left py-2 px-3">Model</th>
                  <th className="text-right py-2 px-3">Duration</th>
                  <th className="text-right py-2 px-3">Tokens In</th>
                  <th className="text-right py-2 px-3">Tokens Out</th>
                  <th className="text-right py-2 px-3">Cost</th>
                  <th className="text-center py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {spans.map((span, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{span.operation}</td>
                    <td className="py-2 px-3 text-gray-600">{span.provider}</td>
                    <td className="py-2 px-3 text-gray-600 text-xs">{span.model_id}</td>
                    <td className="py-2 px-3 text-right">{span.duration_ms}ms</td>
                    <td className="py-2 px-3 text-right">{span.input_tokens?.toLocaleString() || 0}</td>
                    <td className="py-2 px-3 text-right">{span.output_tokens?.toLocaleString() || 0}</td>
                    <td className="py-2 px-3 text-right">${span.cost_est_usd?.toFixed(4)}</td>
                    <td className="py-2 px-3 text-center">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          span.status === 'success'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {span.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TraceDetail;
